import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultModal from './components/modals/DefaultModal';
import { logout } from './api/logout';
import PaginationBar from './components/PaginationBar';
import usePagination10 from './components/hooks/usePagination10';
import Pagination10 from './components/Pagination10';
import { agruparPorCidade } from './utils/agruparPorCidade';

function SelecionaEscola() {
  const [escolaSelecionada, setEscolaSelecionada] = useState(null);
  const [vinculo, setVinculo] = useState([]);
  const [cidadeSelecionada, setCidadeSelecionada] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [sortField, setSortField] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');
  const [user, setUser] = useState(null);
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/');
      return;
    }
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));

    const storedOptions = localStorage.getItem('vinculos_all');
    if (storedOptions) {
      try {
        const parsedOptions = JSON.parse(storedOptions);
        setVinculo(parsedOptions);

        if (parsedOptions.length === 1) {
          const singleOption = parsedOptions[0];
          localStorage.setItem('escola', JSON.stringify(singleOption.escola));
          localStorage.setItem('cnpj', singleOption.escola.cnpj);
          localStorage.setItem('vinculo', JSON.stringify(singleOption));
          navigate('/home');
        } else {
          const grupos = agruparPorCidade(parsedOptions);
          if (grupos.length === 1) setCidadeSelecionada(grupos[0].chave);
        }
      } catch (error) {
        console.error('Erro ao parsear os dados do localStorage:', error);
      }
    }
  }, [navigate]);

  const normalizeString = str =>
    str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const cidades = useMemo(() => agruparPorCidade(vinculo), [vinculo]);
  const cidadeAtual = cidades.find(c => c.chave === cidadeSelecionada) || null;

  const cidadesFiltradas = cidades.filter(c =>
    normalizeString(`${c.municipio} ${c.uf}`).includes(normalizeString(filtro))
  );

  const escolasFiltradas = Array.isArray(vinculo)
    ? vinculo.filter(option => {
        const termo = normalizeString(filtro);
        const casaTermo = normalizeString(option.escola.nome).includes(termo) ||
               normalizeString(option.escola.municipio || '').includes(termo);
        if (!casaTermo) return false;
        if (cidadeSelecionada) {
          const municipio = (option.escola.municipio || '').trim() || 'Não informada';
          const uf = (option.escola.uf || '').trim();
          return `${municipio}|${uf}` === cidadeSelecionada;
        }
        return true;
      })
    : [];

  const handleSort = field => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPagination.onFirstItems();
  };

  const totalItems = escolasFiltradas.length;
  const { setPagination } = usePagination10(totalItems);
  const escolasOrdenadas = [...escolasFiltradas].sort((a, b) => {
    const va = sortField === 'cidade'
      ? normalizeString(`${a.escola.municipio || ''} ${a.escola.uf || ''}`)
      : normalizeString(a.escola.nome);
    const vb = sortField === 'cidade'
      ? normalizeString(`${b.escola.municipio || ''} ${b.escola.uf || ''}`)
      : normalizeString(b.escola.nome);
    const cmp = va.localeCompare(vb, 'pt-BR', { sensitivity: 'base' });
    return cmp * (sortDir === 'asc' ? 1 : -1);
  });
  const selectedEscolas = escolasOrdenadas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const handleLogout = () => {
    const token = localStorage.getItem('token');
    if (token) { logout(token).catch(() => {}); }
    localStorage.clear();
    try { sessionStorage.setItem('justLoggedOut','true'); sessionStorage.removeItem('loginRedirectMessage'); } catch (e) { }
    navigate('/');
  };

  const handleSelect = option => {
    localStorage.setItem('escola', JSON.stringify(option.escola));
    localStorage.setItem('cnpj', option.escola.cnpj);
    localStorage.setItem('vinculo', JSON.stringify(option));
    navigate('/home');
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundImage: `url('/assets/desktop.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Overlay */}
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)' }} />

      {/* Card */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '1000px',
          margin: '1rem',
          borderRadius: '16px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          boxShadow: '0 12px 48px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#198754',
            padding: '1.25rem 1.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
            <img
              src="/assets/logo.png"
              alt="Logo"
              width="48"
              height="48"
              style={{ borderRadius: '10px' }}
            />
            <div>
              <h5 style={{ color: '#fff', margin: 0, fontWeight: 700 }}>Selecione a Escola</h5>
              {user?.nome && (
                <p style={{ color: 'rgba(255,255,255,0.75)', margin: 0, fontSize: '0.8rem' }}>
                  <i className="bi bi-person me-1"></i>{user.nome}
                </p>
              )}
            </div>
          </div>
          <button
            className="btn btn-sm"
            onClick={handleLogout}
            title="Sair do sistema"
            style={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.5)',
              color: '#fff',
              borderRadius: '8px',
            }}
          >
            <i className="bi bi-box-arrow-right me-1"></i>
            Sair
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '1rem 1.5rem 0.5rem' }}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {cidadeSelecionada && (
              <>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => { setCidadeSelecionada(null); setFiltro(''); setEscolaSelecionada(null); }}
                >
                  <i className="bi bi-arrow-left me-1"></i>
                  Cidades
                </button>
                <span className="fw-semibold text-success">
                  <i className="bi bi-geo-alt-fill me-1"></i>
                  Escolas de {cidadeAtual?.municipio}{cidadeAtual?.uf ? `/${cidadeAtual.uf}` : ''}
                </span>
                <span className="text-muted small">({escolasFiltradas.length} {escolasFiltradas.length === 1 ? 'escola' : 'escolas'})</span>
              </>
            )}
            <div
              className={`input-group input-group-sm${cidadeSelecionada ? ' ms-auto' : ''}`}
              style={cidadeSelecionada ? { maxWidth: '300px' } : undefined}
            >
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder={cidadeSelecionada ? 'Buscar por nome da escola...' : 'Buscar por cidade...'}
                value={filtro}
                onChange={e => setFiltro(e.target.value.toUpperCase())}
              />
            </div>
          </div>
        </div>

        {/* Conteúdo: reutiliza a mesma região de rolagem da tabela,
            para não adicionar scroll extra à página */}
        <div style={{ padding: '0 1.5rem' }}>
          <div
            className={`small${cidadeSelecionada ? ' table-responsive-sm shadow-sm' : ''}`}
            style={{ height: '380px', overflowY: 'auto', overflowX: cidadeSelecionada ? undefined : 'hidden', minHeight: 0, borderRadius: 8, padding: cidadeSelecionada ? undefined : '0 0.5rem' }}
          >
            {!cidadeSelecionada ? (
              cidadesFiltradas.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-geo-alt me-2"></i>Nenhuma cidade encontrada.
                </div>
              ) : (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-3" style={{ padding: '0.75rem 0' }}>
                  {cidadesFiltradas.map(cidade => (
                    <div className="col" key={cidade.chave}>
                      <div
                        className="card border-0 shadow-sm h-100"
                        role="button"
                        onClick={() => { setCidadeSelecionada(cidade.chave); setFiltro(''); setEscolaSelecionada(null); }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
                        style={{ borderRadius: 12, cursor: 'pointer', transition: 'transform .12s ease, box-shadow .12s ease' }}
                        title={`Ver escolas de ${cidade.municipio}`}
                      >
                        <div className="card-body d-flex align-items-center gap-3 py-3">
                          <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#198754', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                            <i className="bi bi-geo-alt-fill text-white"></i>
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="fw-semibold" style={{ color: '#198754', wordBreak: 'break-word' }}>
                              {cidade.municipio}{cidade.uf ? `/${cidade.uf}` : ''}
                            </div>
                          </div>
                          <span className="badge rounded-pill bg-success">
                            {cidade.total} {cidade.total === 1 ? 'escola' : 'escolas'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <table className="table table-hover table-striped table-sm small mb-0">
                <thead className="table-secondary sticky-top" style={{ zIndex: 1, '--bs-table-bg': '#198754', '--bs-table-border-color': '#157347', '--bs-table-color': '#fff' }}>
                  <tr>
                    <th className="px-2 d-none d-md-table-cell">CNPJ</th>
                    <th
                      className="px-2"
                      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('nome')}
                      title="Ordenar por Nome da Escola"
                    >
                      Nome da Escola
                      {sortField === 'nome'
                        ? <i className={`bi ${sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down'} ms-1`} />
                        : <i className="bi bi-arrow-down-up ms-1 opacity-50" />}
                    </th>
                    <th className="px-2 d-none d-sm-table-cell">INEP</th>
                    <th
                      className="px-2 d-none d-sm-table-cell"
                      style={{ cursor: 'pointer', minWidth: '160px', whiteSpace: 'nowrap' }}
                      onClick={() => handleSort('cidade')}
                      title="Ordenar por Cidade / UF"
                    >
                      Cidade / UF
                      {sortField === 'cidade'
                        ? <i className={`bi ${sortDir === 'asc' ? 'bi-sort-up' : 'bi-sort-down'} ms-1`} />
                        : <i className="bi bi-arrow-down-up ms-1 opacity-50" />}
                    </th>
                    <th className="px-2 text-center">Acessar</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEscolas.map((option, index) => (
                    <tr
                      key={index}
                      onClick={() => setEscolaSelecionada(option)}
                      style={{ cursor: 'pointer' }}
                      className={escolaSelecionada === option ? 'table-success' : ''}
                    >
                      <td className="px-2 d-none d-md-table-cell align-middle">{option.escola.cnpj}</td>
                      <td className="px-2 align-middle" style={{ fontWeight: escolaSelecionada === option ? 600 : 400 }}>{option.escola.nome}</td>
                      <td className="px-2 d-none d-sm-table-cell align-middle">{option.escola.inep || '—'}</td>
                      <td className="px-2 d-none d-sm-table-cell align-middle">{option.escola.municipio} / {option.escola.uf}</td>
                      <td className="px-2 text-center align-middle">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={e => { e.stopPropagation(); handleSelect(option); }}
                          title="Acessar esta escola"
                        >
                          <i className="bi bi-arrow-right-circle"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {selectedEscolas.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        <i className="bi bi-search me-2"></i>Nenhuma escola encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination */}
        {cidadeSelecionada && (
          <div
            style={{
              padding: '0.6rem 1.5rem',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
            }}
          >
            <Pagination10 {...setPagination} />
          </div>
        )}
      </div>

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
    </div>
  );
}

export default SelecionaEscola;
